import type { CSSProperties } from "react";

import { colorTheme } from "@utils";

import type { PivotFormatRule } from "./types";

export interface ValueStats {
  min: number;
  max: number;
}

const tones = {
  success: { color: "success", background: "successL5" },
  warning: { color: "warning", background: "warningL5" },
  danger: { color: "danger", background: "dangerL5" },
} as const;

const compare = (n: number, operator: string, target: number) => {
  switch (operator) {
    case ">":
      return n > target;
    case "<":
      return n < target;
    case ">=":
      return n >= target;
    default:
      return n <= target;
  }
};

const scale = (n: number, stats: ValueStats) =>
  stats.max === stats.min
    ? 0.6
    : 0.08 + ((n - stats.min) / (stats.max - stats.min)) * 0.52;

export const formatRuleStyle = (
  rules: PivotFormatRule[] | undefined,
  n: number | null,
  stats: ValueStats | undefined,
): { style?: CSSProperties; icon?: "up" | "down" } => {
  if (!rules?.length || n === null) return {};
  const style: CSSProperties = {};
  let icon: "up" | "down" | undefined;
  for (const rule of rules) {
    switch (rule.type) {
      case "colorScale":
        if (stats) {
          style.backgroundColor = `color-mix(in srgb, ${colorTheme("primaryL1")} ${Math.round(scale(n, stats) * 100)}%, transparent)`;
        }
        break;
      case "dataBar":
        if (stats && stats.max > 0) {
          const width = Math.max(0, Math.min(100, (n / stats.max) * 100));
          style.backgroundImage = `linear-gradient(to right, ${colorTheme("primaryL3")} ${width}%, transparent ${width}%)`;
        }
        break;
      case "signColors":
        if (n !== 0) style.color = colorTheme(n > 0 ? "success" : "danger");
        break;
      case "arrows":
        if (n !== 0) icon = n > 0 ? "up" : "down";
        break;
      case "threshold":
        if (compare(n, rule.operator, rule.value)) {
          style.color = colorTheme(tones[rule.tone].color);
          style.backgroundColor = colorTheme(tones[rule.tone].background);
        }
        break;
    }
  }
  return { style, icon };
};

export const FORMAT_PRESETS: {
  key: string;
  label: string;
  rules: PivotFormatRule[];
}[] = [
  { key: "colorScale", label: "Color scale", rules: [{ type: "colorScale" }] },
  { key: "dataBar", label: "Data bars", rules: [{ type: "dataBar" }] },
  {
    key: "signColors",
    label: "Green / red by sign",
    rules: [{ type: "signColors" }],
  },
  {
    key: "arrows",
    label: "Up / down arrows",
    rules: [{ type: "arrows" }, { type: "signColors" }],
  },
];
